import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { docs, type TagRun } from "../lib/api";
import { ApiError } from "../lib/http";
import type { Me } from "../lib/me";
import { tagRunNote } from "../pages/documentViewFlow";
import { Explainer } from "./Explainer";
import { RoleButton } from "./RequireRole";

/**
 * The package tag run: read every page for every tag, not only the index's, and read each table
 * page row by row with Claude on the subscription. Polls while it runs.
 */
export function TagRunCard({ pkg, me }: { pkg: string; me: Me | null }) {
  const [run, setRun] = useState<TagRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let live = true;
    setRun(null); setError(null);
    docs.tagRun(pkg)
      .then((r) => { if (live) setRun(r); })
      .catch((e) => { if (live && !(e instanceof ApiError && e.status === 404)) setError((e as Error).message); });
    return () => { live = false; };
  }, [pkg]);

  useEffect(() => {
    if (run?.status !== "running") return;
    const timer = window.setInterval(() => {
      docs.tagRun(pkg).then(setRun).catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [pkg, run?.status]);

  async function start() {
    setStarting(true); setError(null);
    try {
      setRun(await docs.startTagRun(pkg));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  }

  const pill = run?.status === "ready" ? "ok" : run?.status === "failed" ? "fail" : "info";
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="section-title"><h3>Every tag, and the tables</h3></div>
      <Explainer>
        The content index looks for the tags the master index names. This run reads every page for
        any tag written on it, marks each one, and labels the tags the index does not name. A page
        whose tags line up in a column is read once by Claude as a table, every row whole: tag,
        service, range, make, model, serial. Each value is checked against the page and lands in
        the register as a candidate for a person to accept.
      </Explainer>
      <div className="row" style={{ marginTop: 14, gap: 14, flexWrap: "wrap" }}>
        {run && (
          <span className={`pill ${pill}`}>
            {run.status === "running" ? <span className="row" style={{ gap: 6 }}><Loader2 size={13} aria-hidden="true" /> reading</span> : run.status}
          </span>
        )}
        <span className="muted" style={{ fontSize: 13 }} aria-live="polite">{tagRunNote(run)}</span>
        {run && run.table_reader !== "claude_subscription" && (
          <span className="muted" style={{ fontSize: 13 }}>Table pages cannot be read here: {run.table_reader}.</span>
        )}
        <span style={{ marginLeft: "auto" }}>
          <RoleButton role="engineer" me={me} className={run ? "ghost" : undefined}
                      disabled={starting || run?.status === "running"} onClick={() => void start()}>
            {run ? "Run again" : "Find every tag and read tables"}
          </RoleButton>
        </span>
      </div>
      {error && <p className="muted" role="alert" style={{ fontSize: 13, marginTop: 8 }}>{error}</p>}
    </div>
  );
}
